/**
 * "Which resources does this questionnaire assign, and is each one published?"
 *
 * Additive by design: nothing else on the Assigned Questionnaires page changes, and
 * removing this file plus its button restores the previous list exactly.
 *
 * Why it exists: publish already reports the same findings, but in a banner that the
 * next action clears and a refresh loses — so a stale terminal is visible once and
 * then unrecoverable. This makes the same answer available on demand, and adds the
 * inventory that no screen showed at all: the resources that ARE ready.
 *
 * Blocking and advisory must not read alike. A blocking finding means publishing will
 * be refused; an advisory one means it will succeed with something still wrong. The
 * marker, the wording and the ordering all carry that difference.
 */
import { adminButton, adminTokens } from '../admin-ui';
import { formatDateTime } from '../utils/formatDateTime';
import {
  useQuestionnaireReadiness,
  type AssignedQuestionnaire,
  type DeadEndBranch,
  type TerminalReadiness,
  type TerminalReadinessState,
} from '../api/questionnaireApi';

/**
 * Amber for advisory findings, never the error red: they do not block anything.
 */
const ADVISORY_COLOR = adminTokens.status.warning.text;

const STATE_MARKER: Record<TerminalReadinessState, string> = {
  blocking: '✕',
  advisory: '⚠',
  ready: '✓',
};

const STATE_LABEL: Record<TerminalReadinessState, string> = {
  blocking: 'Blocks publishing',
  advisory: 'Publishes with a warning',
  ready: 'Ready',
};

const CONTENT_KIND: Record<NonNullable<TerminalReadiness['contentType']>, string> = {
  programs: 'Program',
  microlessons: 'Microlesson',
};

/**
 * Names the offending answers, so the author can go and find them.
 *
 * A bare count is not actionable — "1 answer leads nowhere" leaves them hunting through a
 * tree they can only see in wh-frontend. Capped at two, because past that the list stops
 * being a pointer and starts being the tree.
 */
const describeDeadEnds = (deadEnds: DeadEndBranch[]): string => {
  const named = deadEnds
    .map((deadEnd) => (deadEnd.questionText && deadEnd.choiceText
      ? `“${deadEnd.choiceText}” on “${deadEnd.questionText}”`
      : deadEnd.choiceText
        ? `“${deadEnd.choiceText}”`
        : null))
    .filter((label): label is string => label !== null);
  if (named.length === 0) return '';
  const shown = named.slice(0, 2).join(', ');
  return `: ${shown}${named.length > 2 ? `, and ${named.length - 2} more` : ''}`;
};

const relativeTime = (iso: string): string => {
  const elapsed = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(elapsed) || elapsed < 0) return 'just now';
  const minutes = Math.floor(elapsed / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes === 1) return '1 minute ago';
  if (minutes < 60) return `${minutes} minutes ago`;
  const hours = Math.floor(minutes / 60);
  return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
};

export function QuestionnaireReadinessPanel({
  questionnaire,
  onRecheck,
}: {
  questionnaire: AssignedQuestionnaire;
  /** Invalidates the cached read so the next render fetches again. */
  onRecheck: () => void;
}) {
  const { data, isLoading, isError, isFetching } = useQuestionnaireReadiness(questionnaire, true);

  const muted = { fontSize: adminTokens.typography.label.size, color: adminTokens.text.secondary };

  if (isLoading) {
    return <p style={{ ...muted, margin: '12px 0 0' }}>Checking which resources are published…</p>;
  }

  if (isError || !data) {
    return (
      <p style={{ ...muted, margin: '12px 0 0' }}>
        Could not check the assigned resources. This says nothing about whether they are
        published — try again.
      </p>
    );
  }

  const { terminals, questionCount, noAssignmentOutcomes, inventoryUnavailable } = data;

  return (
    <div style={{ marginTop: '12px', borderTop: `1px solid ${adminTokens.border.subtle}`, paddingTop: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }}>
        <p style={{ ...muted, margin: 0 }}>
          {inventoryUnavailable
            ? `${terminals.length} assigned resource${terminals.length === 1 ? '' : 's'} need attention`
            : [
              `${questionCount} question${questionCount === 1 ? '' : 's'}`,
              `${terminals.length} assigned resource${terminals.length === 1 ? '' : 's'}`,
              ...(noAssignmentOutcomes > 0
                ? [`${noAssignmentOutcomes} outcome${noAssignmentOutcomes === 1 ? '' : 's'} assign${noAssignmentOutcomes === 1 ? 's' : ''} no training`]
                : []),
            ].join(' · ')}
        </p>
        {/*
          * An authoring defect rather than a delivery one, so it sits above the resource
          * list instead of on a row — no resource is at fault, the tree is.
          *
          * Amber, not red: it does not block publishing, and it may well be deliberate.
          * Only the author knows whether that branch was meant to end there.
          */}
        {data.deadEnds.length > 0 && (
          <p style={{ ...muted, margin: 0, flexBasis: '100%', color: ADVISORY_COLOR }}>
            {data.deadEnds.length === 1
              ? '1 answer leads nowhere'
              : `${data.deadEnds.length} answers lead nowhere`}
            {describeDeadEnds(data.deadEnds)}
            {' — a learner reaching it is asked every question again next time, because no completion is recorded.'}
          </p>
        )}
        <span style={{ ...muted }}>
          checked {relativeTime(data.checkedAt)}
          {' · '}
          <button
            type="button"
            onClick={onRecheck}
            disabled={isFetching}
            style={{
              ...adminButton.link,
              ...(isFetching ? adminButton.disabled : {}),
            }}
          >
            {isFetching ? 'Re-checking…' : 'Re-check'}
          </button>
        </span>
      </div>

      {inventoryUnavailable && (
        <p style={{ ...muted, margin: '8px 0 0' }}>
          Only the problems are listed — this server does not report the full resource list yet.
        </p>
      )}

      {terminals.length === 0 ? (
        <p style={{ ...muted, margin: '8px 0 0' }}>
          {questionCount === 0
            ? 'This questionnaire has no content yet.'
            : 'This questionnaire assigns no training on any branch.'}
        </p>
      ) : (
        <ul style={{ listStyle: 'none', margin: '8px 0 0', padding: 0 }}>
          {terminals.map((terminal) => (
            <li
              key={terminal.nodeId}
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto minmax(0, 260px) minmax(0, 1fr)',
                gap: '4px 12px',
                alignItems: 'baseline',
                padding: '4px 0',
                borderTop: `1px solid ${adminTokens.border.subtle}`,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  fontSize: '13px',
                  lineHeight: '20px',
                  color:
                    terminal.state === 'blocking'
                      ? adminTokens.status.error.text
                      : terminal.state === 'advisory'
                        ? ADVISORY_COLOR
                        : adminTokens.status.success.text,
                }}
              >
                {STATE_MARKER[terminal.state]}
              </span>
              <p style={{ margin: 0, fontSize: adminTokens.typography.body.size, color: adminTokens.text.primary }}>
                {terminal.resourceName ?? `Node ${terminal.nodeId}`}
                {terminal.contentType && (
                  <span style={{ ...muted, display: 'block' }}>
                    {CONTENT_KIND[terminal.contentType]}
                    {terminal.authorAiCourseId
                      ? ` · ${terminal.authorAiCourseId}`
                      : terminal.contentId
                        ? ` · ${terminal.contentId}`
                        : ''}
                  </span>
                )}
                {/*
                  * Screen readers get the state as words, not as a glyph — but only
                  * where the visible text is the server's reason. A ready row's visible
                  * text IS the state, so repeating it here would read it twice.
                  */}
                {/*
                  * The state in words, for screen readers only — the marker glyph beside
                  * it is aria-hidden.
                  *
                  * Rendered on EVERY row, including ready ones. It used to be suppressed
                  * there because the visible text was the word "Ready" and repeating it
                  * would read twice; now a ready row shows only a publish date, so
                  * without this a screen reader hears a timestamp with nothing saying
                  * the resource is fine.
                  */}
                <span style={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
                  {STATE_LABEL[terminal.state]}
                </span>
              </p>
              {/*
                * One paragraph per problem. A resource can be wrong in two ways at once
                * and the remedies differ, so they must not be run together into a
                * single block of text the author has to unpick.
                */}
              <div>
                {terminal.reasons.length === 0 ? (
                  /*
                   * No "Ready" here: the ✓ already says the resource is fine, and the
                   * publish date is the part an author can act on — one published this
                   * morning and one published months ago are both "ready", and only one
                   * invites a second look. The word added length without adding meaning.
                   *
                   * Absolute and formatted by the same helper as the delivery badge above,
                   * so the same kind of fact reads the same way a few pixels apart.
                   */
                  <p style={{ ...muted, margin: 0 }}>
                    {terminal.publishedAt
                      ? `published ${formatDateTime(terminal.publishedAt)}`
                      // The bytes ARE hosted — this row would be blocking otherwise. What
                      // is missing is the deploy record beside them, so the freshness
                      // checks could not run either. Never "not published".
                      : 'Publish date unknown'}
                  </p>
                ) : (
                  terminal.reasons.map((reason, index) => (
                    <p key={reason} style={{ ...muted, margin: index === 0 ? 0 : '4px 0 0' }}>{reason}</p>
                  ))
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
